import { Card, CardContent, Skeleton } from '@startupquickstart/frontend';

import { EventRealtimeToggles } from '@/components/events/EventRealtimeToggles';
import {
  LiveActivityPanel,
  LiveScoreboardPanel,
  LiveStatusBar,
  PendingReviewsPanel,
  SlideshowCurationPanel,
  TeamLocationsPanel,
} from '@/components/events/live';
import { useDisplaySocket } from '@/hooks/useDisplaySocket';
import { useEventContext } from '@/pages/events/useEventContext';

/**
 * Live command center: everything a facilitator needs while a hunt is
 * running on one screen — status + clock + lifecycle controls up top,
 * then scoreboard, realtime activity, the photo-review gauge, and the
 * live event switches.
 */
export function EventLiveTab() {
  const { event, isLoading } = useEventContext();
  const { status: connectionStatus } = useDisplaySocket(event?.id);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-14 rounded-xl" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event not found</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto -m-4 sm:-m-6 p-4 sm:p-6 space-y-4">
      <LiveStatusBar event={event} connectionStatus={connectionStatus} />

      {/* Where teams are on the ground — the thing facilitators lose when
          moving off the old SmartHunts workflow. Full width above the
          three-column row, since a map is unreadable in a third of a
          column. */}
      <TeamLocationsPanel
        eventId={event.id}
        enforcesTogetherness={event.requireTeamTogether ?? false}
      />

      {/* Slideshow curation — sits with the other live tools because it's
          used minutes before the room watches, not in event setup. */}
      <SlideshowCurationPanel eventId={event.id} />

      <div className="grid items-start gap-4 lg:grid-cols-3">
        <LiveScoreboardPanel eventId={event.id} />
        <LiveActivityPanel eventId={event.id} />
        <div className="space-y-4">
          {/* The review queue only exists when points are gated on review —
              with instant awards there's nothing for a facilitator to decide,
              so the panel hides instead of implying revocable points. */}
          {event.requireMediaReview && <PendingReviewsPanel eventId={event.id} />}
          <Card className="gap-0 overflow-hidden py-0">
            <div className="border-b px-4 py-3">
              <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
                Live settings
              </h3>
            </div>
            <CardContent className="p-4">
              <EventRealtimeToggles event={event} className="grid-cols-1 gap-4 sm:grid-cols-1" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
